/**
 * New Password Required Form
 * Handles NEW_PASSWORD_REQUIRED challenge when admin creates user
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import { completeNewPasswordChallenge } from '../../services/auth/authService';

/**
 * Validate password meets Cognito requirements
 */
function validatePassword(password) {
  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number';
  }
  return null;
}

export default function NewPasswordForm({ challengeResult, username, onSuccess, onCancel }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const result = await completeNewPasswordChallenge(challengeResult, newPassword, username);

      if (onSuccess) {
        onSuccess(result.user);
      }
    } catch (err) {
      console.error('New password error:', err);
      setError(err.message || 'Failed to set new password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Set New Password
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Your account requires a new password. Please choose a secure password.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={loading}
        required
        autoFocus
        helperText="Minimum 8 characters with uppercase, lowercase, and number"
      />

      <TextField
        fullWidth
        label="Confirm New Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={loading}
        required
      />

      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ flex: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Set Password'}
        </Button>
      </Box>
    </Box>
  );
}
